// src/contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));
  
  const isRefreshing = useRef(false);

  // Configurer axios avec le token
  useEffect(() => {
    if (accessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [accessToken]);

  // Vérifier si le token est expiré
  const isTokenExpired = useCallback((token) => {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }, []);

  // Temps restant avant expiration
  const getTokenRemainingTime = useCallback((token) => {
    if (!token) return 0;
    try {
      const decoded = jwtDecode(token);
      const remaining = decoded.exp * 1000 - Date.now();
      return remaining > 0 ? remaining : 0;
    } catch {
      return 0;
    }
  }, []);

  // Déconnexion
  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_permission_level');
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Rafraîchir le token
  const refreshAccessToken = useCallback(async () => {
    if (isRefreshing.current) return false;
    
    const currentRefreshToken = localStorage.getItem('refresh_token');
    if (!currentRefreshToken) {
      logout();
      return false;
    }
    
    isRefreshing.current = true;
    
    try {
      const response = await axios.post(`${API_BASE}/auth/refresh`, {
        refresh_token: currentRefreshToken
      });
      
      const { access_token } = response.data;
      localStorage.setItem('access_token', access_token);
      setAccessToken(access_token);
      return true;
    } catch (error) {
      logout();
      return false;
    } finally {
      isRefreshing.current = false;
    }
  }, [logout]);

  // Timer pour rafraîchir automatiquement
  useEffect(() => {
    if (!accessToken) return;
    
    const remainingTime = getTokenRemainingTime(accessToken);
    const refreshTime = Math.max(remainingTime - 60000, 0);
    
    if (remainingTime <= 0) {
      refreshAccessToken();
      return;
    }
    
    const timer = setTimeout(() => {
      refreshAccessToken();
    }, refreshTime);
    
    return () => clearTimeout(timer);
  }, [accessToken, refreshAccessToken, getTokenRemainingTime]);

  // Charger l'utilisateur
  const loadUser = useCallback(async (token) => {
    try {
      const response = await axios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      
      // Stocker le niveau de permission dans localStorage
      if (userData.permissions && userData.permissions.level) {
        localStorage.setItem('user_permission_level', userData.permissions.level.toString());
      }
      
      setUser(userData);
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  // Initialisation
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      
      const token = localStorage.getItem('access_token');
      const storedRefreshToken = localStorage.getItem('refresh_token');
      
      if (!token || isTokenExpired(token)) {
        if (storedRefreshToken) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            const newToken = localStorage.getItem('access_token');
            await loadUser(newToken);
          }
        }
      } else {
        const success = await loadUser(token);
        if (!success) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            const newToken = localStorage.getItem('access_token');
            await loadUser(newToken);
          }
        }
      }
      
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE}/auth/login`, {
        email,
        password
      });
      
      const { access_token, refresh_token, staff } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Stocker le niveau de permission
      if (staff.permissions && staff.permissions.level) {
        localStorage.setItem('user_permission_level', staff.permissions.level.toString());
      }
      
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
      setUser(staff);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const hasMinLevel = useCallback((level) => {
    // Vérifier d'abord dans localStorage
    const storedLevel = localStorage.getItem('user_permission_level');
    if (storedLevel && parseInt(storedLevel) >= level) {
      return true;
    }
    // Sinon vérifier dans l'objet user
    if (user?.permissions?.level) {
      return user.permissions.level >= level;
    }
    return false;
  }, [user]);

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    hasMinLevel,
    accessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};